import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useMutation } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { BasicTextEditorHandle } from '@factorialco/factorial-one-react/dist/experimental'
import { Tabs } from '@factorialco/factorial-one-react/dist/experimental'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  MeetingsLiveCompanionDocument,
  MeetingsLiveCompanionMutation,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { useMeetingNotes } from 'modules/meetings/lib/hooks/useMeetingNotes'

import { AIChat } from './components/AIChat'
import { Notes } from './components/Notes'
import { Transcript } from './components/Transcript'

export const SideBar = () => {
  const { id: meetingId } = useParams<{ id: string }>()

  const { setNotes, notes } = useMeetingNotes()

  const [activeTab, setActiveTab] = useState<'notes' | 'transcript' | 'ai_chat'>('notes')
  const textEditorRef = useRef<BasicTextEditorHandle>(null)

  const [liveCompanionEnabledPersisted, setLiveCompanionEnabledPersisted] =
    useLocalStorage<boolean>(LOCAL_STORAGE_KEYS.meetingLiveCompanion, false)

  const [liveCompanionEnabled, setLiveCompanionEnabled] = useState<boolean>(
    !!liveCompanionEnabledPersisted
  )

  useEffect(() => {
    setLiveCompanionEnabled(!!liveCompanionEnabledPersisted)
  }, [liveCompanionEnabledPersisted])

  const updateLiveCompanionEnabled = (value: boolean) => {
    setLiveCompanionEnabled(value)
    setLiveCompanionEnabledPersisted(value)
  }

  const [liveCompanion, { loading: loadingLiveCompanion }] =
    useMutation<MeetingsLiveCompanionMutation>(MeetingsLiveCompanionDocument)

  const fetchLiveCompanion = useCallback(async () => {
    const previousNotes = notes || ''

    try {
      const { data } = await liveCompanion({
        variables: {
          meetingId: Number(meetingId),
        },
      })

      if (data?.meetingsMutations?.generateMeetingsLiveCompanion?.liveCompanion) {
        const rawContent =
          data.meetingsMutations.generateMeetingsLiveCompanion.liveCompanion.formattedContent

        const parsedContent = JSON.parse(rawContent)

        setNotes(rawContent)
        textEditorRef?.current?.setContent(parsedContent)
      }
    } catch (error) {
      setNotes(previousNotes)
    }
  }, [liveCompanion, meetingId, setNotes, notes])

  useEffect(() => {
    if (!meetingId || !liveCompanionEnabled) return

    fetchLiveCompanion()

    const interval = setInterval(fetchLiveCompanion, 2 * 60 * 1000)

    return () => clearInterval(interval)
  }, [liveCompanionEnabled, meetingId])

  return (
    <>
      <Box paddingTop="s12">
        <Tabs
          activeTabId={activeTab}
          tabs={[
            {
              index: true,
              id: 'notes',
              label: i18n.t('meetings.room.sidebar.notes'),
              onClick: () => setActiveTab('notes'),
            },
            {
              id: 'ai_chat',
              label: i18n.t('meetings.room.sidebar.ai_chat'),
              onClick: () => setActiveTab('ai_chat'),
            },
            {
              id: 'transcript',
              label: i18n.t('meetings.room.sidebar.transcript'),
              onClick: () => setActiveTab('transcript'),
            },
          ]}
        />
      </Box>
      {activeTab === 'transcript' && <Transcript />}
      {activeTab === 'notes' && (
        <Notes
          liveCompanionEnabled={liveCompanionEnabled}
          setLiveCompanionEnabled={updateLiveCompanionEnabled}
          loadingLiveCompanion={loadingLiveCompanion}
          textEditorRef={textEditorRef}
        />
      )}
      {activeTab === 'ai_chat' && <AIChat textEditorRef={textEditorRef} />}
    </>
  )
}
